import { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { Bot, Send, Loader2 } from "lucide-react";

export default function AiAssistant() {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => {
    client.get("/assistant/history/default").then((r) => setMsgs(r.data.messages || [])).catch(() => {});
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: t }, { role: "assistant", text: "" }]);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: t, session: "default" }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const ln of lines) {
          if (!ln.startsWith("data: ")) continue;
          const d = JSON.parse(ln.slice(6));
          if (d.delta) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + d.delta }; return c; });
          if (d.error) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: `Error: ${d.error}` }; return c; });
        }
      }
    } catch {
      setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: "Connection error — try again." }]);
    }
    setBusy(false);
  };
  return (
    <Layout>
      <PageHeader title="AI Assistant" sub="ChatGPT (GPT-5.4) · procedures, report wording, guidance · never makes dispatch/legal/medical decisions" icon={Bot} />
      <div className="p-4 max-w-3xl mx-auto flex flex-col" style={{ height: "calc(100vh - 130px)" }} data-testid="ai-assistant">
        <div className="flex-1 overflow-y-auto space-y-2 mb-3">
          {msgs.map((m, i) => (
            <div key={i} className={`max-w-[85%] rounded-sm px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user"
              ? "ml-auto bg-[#22D3EE] text-black" : "bg-[#141518] border border-[#2C2D35] text-[#E5E7EB]"}`}
              data-testid={`assistant-msg-${m.role}`}>
              {m.text || (busy && i === msgs.length - 1 ? <Loader2 size={14} className="animate-spin" /> : "")}
            </div>))}
          {!msgs.length && <div className="font-mono text-xs text-[#6B7280] text-center mt-10">Ask about procedures, call types, report wording, radio phrasing or how to use the system.</div>}
          <div ref={endRef} />
        </div>
        <div className="flex gap-2">
          <input data-testid="assistant-input" value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask the assistant…"
            className="flex-1 bg-[#0d0e11] border border-[#2C2D35] px-3 py-2.5 text-sm font-mono text-white rounded-sm outline-none focus:border-[#22D3EE]" />
          <button data-testid="assistant-send" onClick={send} disabled={busy}
            className="px-4 py-2 bg-[#22D3EE] text-black font-head font-bold rounded-sm disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          </button>
        </div>
      </div>
    </Layout>
  );
}
